import { useWidgetGlide } from "./use-widget-glide";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useCornerItem, type Corner } from "@softmax/web-features/site";
import type { Clip } from "@softmax/avatar/character";
import { readMountedElement } from "../read-mounted-element";
import { readStoredCorner } from "./widget-corner";
import { startDrag } from "./widget-drag";
import { pickHoverClip, reducedMotion } from "./widget-motion";
type Facing = "left" | "right";
export function useWidgetPosition({
  storageKey,
  hasCogent,
}: Readonly<{
  storageKey: string;
  hasCogent: boolean;
}>) {
  const root = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLDivElement>(null);
  const avatar = useRef<HTMLButtonElement>(null);
  const dragged = useRef(false);
  const dragging = useRef(false);
  const endDrag = useRef<(() => void) | null>(null);
  const letGo = useRef<{
    corner: Corner;
    rect: DOMRect;
  } | null>(null);
  const [corner, setCorner] = useState<Corner>(() =>
    readStoredCorner(storageKey),
  );
  const [carriedFacing, setFacing] = useState<Facing | null>(null);
  const facing = carriedFacing ?? (corner.endsWith("left") ? "right" : "left");
  const [clip, setClip] = useState<Clip | null>(null);
  const lastClip = useRef<Clip | null>(null);
  const { glide, glideAnimation } = useWidgetGlide({ root, dock, setClip });
  const host = useCornerItem({ corner, order: 2 });
  useEffect(
    () => () => {
      endDrag.current?.();
    },
    [],
  );
  useLayoutEffect(() => {
    const pending = letGo.current;

    if (pending === null || pending.corner !== corner) return;

    letGo.current = null;
    glide(pending.rect);
  }, [corner, glide]);
  const moveTo = ({
    next,
    from,
  }: Readonly<{ next: Corner; from: DOMRect }>) => {
    if (next === corner) return glide(from);

    letGo.current = { corner: next, rect: from };
    localStorage.setItem(storageKey, next);
    setCorner(next);
  };
  const playClip = () => {
    if (dragging.current || reducedMotion() || !hasCogent) return;

    const next = pickHoverClip(lastClip.current);
    lastClip.current = next;
    setClip(next);
  };
  const leave = () =>
    setClip((current) =>
      dragging.current || current === "fall" || current === "land"
        ? current
        : null,
    );
  const consumeDrag = () => {
    const consumed = dragged.current;
    dragged.current = false;
    return consumed;
  };
  const onPointerDown = (event: ReactPointerEvent) =>
    startDrag({
      event,
      context: {
        root: readMountedElement(root),
        dock: readMountedElement(dock),
        glideAnimation,
        dragged,
        dragging,
        endDrag,
        setClip,
        setFacing,
        moveTo,
        glide,
      },
    });
  return {
    root,
    dock,
    avatar,
    host,
    corner,
    facing,
    clip,
    setClip,
    moveTo,
    playClip,
    leave,
    consumeDrag,
    onPointerDown,
  };
}
export type WidgetPosition = ReturnType<typeof useWidgetPosition>;
